import { LogOut } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './auth.js';
import { AppLayout } from './components/AppLayout.js';
import { FullPageLoading } from './components/shared.js';
import { Button } from './components/ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card.js';

const ApplicationsPage = lazy(() => import('./pages/ApplicationsPage.js').then((module) => ({ default: module.ApplicationsPage })));
const ApplicationDetailPage = lazy(() => import('./pages/ApplicationDetailPage.js').then((module) => ({ default: module.ApplicationDetailPage })));
const AuditPage = lazy(() => import('./pages/AuditPage.js').then((module) => ({ default: module.AuditPage })));
const DashboardAnalysisPage = lazy(() => import('./pages/DashboardAnalysisPage.js').then((module) => ({ default: module.DashboardAnalysisPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage.js').then((module) => ({ default: module.DashboardPage })));
const LoginPage = lazy(() => import('./pages/PublicPages.js').then((module) => ({ default: module.LoginPage })));
const RecoveryPage = lazy(() => import('./pages/PublicPages.js').then((module) => ({ default: module.RecoveryPage })));
const SetPasswordPage = lazy(() => import('./pages/PublicPages.js').then((module) => ({ default: module.SetPasswordPage })));
const SecurityPage = lazy(() => import('./pages/SecurityPage.js').then((module) => ({ default: module.SecurityPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage.js').then((module) => ({ default: module.SettingsPage })));
const SettingsEmailPage = lazy(() => import('./pages/SettingsEmailPage.js').then((module) => ({ default: module.SettingsEmailPage })));
const UserDetailPage = lazy(() => import('./pages/UserDetailPage.js').then((module) => ({ default: module.UserDetailPage })));
const UserAccessPage = lazy(() => import('./pages/UserAccessPage.js').then((module) => ({ default: module.UserAccessPage })));
const UsersPage = lazy(() => import('./pages/UsersPage.js').then((module) => ({ default: module.UsersPage })));

function ProtectedRoute() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoading />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (!user.adminAccess) return <NoConsoleAccess />;
  return <Outlet />;
}

/**
 * Sem acesso à consola não há para onde navegar — /login devolve quem tem sessão a esta página.
 * Daí o botão de terminar sessão: é a única saída para entrar com outra conta.
 */
function NoConsoleAccess() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const signOut = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="grid min-h-svh place-items-center bg-muted/30 p-6">
      <Card className="max-w-md">
        <CardHeader>
          <CardTitle>Sem acesso à consola</CardTitle>
          <CardDescription>A conta {user?.email} não tem acesso à plataforma de gestão.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 text-sm text-muted-foreground">
          Contacte um administrador para obter acesso, ou termine a sessão para entrar com outra conta.
          <Button variant="outline" className="self-start" onClick={() => void signOut()}>
            <LogOut data-icon="inline-start" /> Terminar sessão
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export function App() {
  return <Suspense fallback={<FullPageLoading />}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<RecoveryPage />} />
      <Route path="/activate" element={<SetPasswordPage mode="activate" />} />
      <Route path="/reset-password" element={<SetPasswordPage mode="reset" />} />
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="/analytics/audit" element={<DashboardAnalysisPage kind="audit" />} />
          <Route path="/analytics/sessions" element={<DashboardAnalysisPage kind="sessions" />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/users/:userId" element={<UserDetailPage />} />
          <Route path="/users/:userId/access" element={<UserAccessPage />} />
          <Route path="/applications" element={<ApplicationsPage />} />
          <Route path="/applications/:applicationId" element={<ApplicationDetailPage section="overview" />} />
          <Route path="/applications/:applicationId/resources" element={<ApplicationDetailPage section="resources" />} />
          <Route path="/applications/:applicationId/permissions" element={<ApplicationDetailPage section="permissions" />} />
          <Route path="/applications/:applicationId/roles" element={<ApplicationDetailPage section="roles" />} />
          {/*
            TODO: Reativar /requests quando existir uma área pessoal onde os
            utilizadores possam criar e acompanhar os próprios pedidos de acesso.
            RequestsPage.tsx e os endpoints permanecem preservados para essa fase.
          */}
          <Route path="/audit" element={<AuditPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/email" element={<SettingsEmailPage />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </Suspense>;
}
